import { CSSProperties } from "react";

const DEFAULT_WIDTHS = [480, 800, 1200, 1600];

interface ResponsiveImageProps {
  /** Base path (with or without extension). Extension is stripped to build variant paths. */
  src: string;
  alt: string;
  width?: number;
  height?: number;
  /** Responsive widths to include in srcSet. Defaults to [480, 800, 1200, 1600]. */
  widths?: number[];
  /** CSS sizes attribute for responsive images. */
  sizes?: string;
  className?: string;
  style?: CSSProperties;
  /** When true, sets loading="eager" and fetchPriority="high" for LCP images. */
  priority?: boolean;
  /** Extra data-testid attribute for testing. */
  "data-testid"?: string;
  /** aria-hidden for decorative images */
  "aria-hidden"?: boolean | "true" | "false";
}

/**
 * Strips the file extension from a path, returning {base, ext}.
 * Handles .png, .jpg, .jpeg, .webp, .avif.
 * If no recognized extension, returns the full path as base with empty ext.
 */
function splitExt(src: string): { base: string; ext: string } {
  const match = src.match(/^(.+)\.(png|jpe?g|webp|avif)$/i);
  if (match) return { base: match[1], ext: match[2] };
  return { base: src, ext: "" };
}

/**
 * Builds a srcSet string for the given format.
 * E.g. "/images/hero-480.avif 480w, /images/hero-800.avif 800w, ..."
 * Also includes the full-size variant without a width suffix.
 */
function buildSrcSet(base: string, widths: number[], format: string): string {
  const parts = widths.map((w) => `${base}-${w}.${format} ${w}w`);
  // Include full-size as the largest descriptor
  parts.push(`${base}.${format}`);
  return parts.join(", ");
}

/**
 * Renders a `<picture>` element with AVIF and WebP sources plus an `<img>` fallback.
 *
 * Expects that the optimization script has already generated the variant files
 * (e.g., hero-480.avif, hero-800.webp, etc.) alongside the source image.
 *
 * For dynamic images (e.g. from a CMS/DB), the component will still render valid
 * markup — browsers will fall through to the `<img>` fallback if variants don't exist.
 */
export function ResponsiveImage({
  src,
  alt,
  width,
  height,
  widths = DEFAULT_WIDTHS,
  sizes,
  className,
  style,
  priority = false,
  "data-testid": testId,
  "aria-hidden": ariaHidden,
}: ResponsiveImageProps) {
  // External URLs (Webflow CDN blog thumbs, etc.) don't have our -480/-800
  // variants. If we emit them in srcSet, <picture> picks a non-existent URL
  // based on viewport sizing and the browser does NOT fall back to <img> —
  // it shows a broken image. So for external src, render a plain <img>.
  const isExternal = /^https?:\/\//i.test(src);
  if (isExternal) {
    return (
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        className={className}
        style={style}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : undefined}
        decoding="async"
        data-testid={testId}
        aria-hidden={ariaHidden}
      />
    );
  }

  const { base, ext } = splitExt(src);
  const origExt = ext || "jpg"; // fallback for the <img> src

  // Build the fallback src — use original path if it had an extension, else append jpg
  const fallbackSrc = ext ? src : `${base}.${origExt}`;

  const avifSrcSet = buildSrcSet(base, widths, "avif");
  const webpSrcSet = buildSrcSet(base, widths, "webp");

  return (
    <picture>
      <source type="image/avif" srcSet={avifSrcSet} sizes={sizes} />
      <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />
      <img
        src={fallbackSrc}
        alt={alt}
        width={width}
        height={height}
        className={className}
        style={style}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : undefined}
        decoding="async"
        data-testid={testId}
        aria-hidden={ariaHidden}
      />
    </picture>
  );
}
